import { useMemo } from "react";
import { useSelector } from "react-redux";

import { DEFAULT_FONT_SIZE } from "constants/chart.constants";
import { RootState } from "store/rootReducer";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import { selectNetworkDepthLegend, useMapStore } from "stores/map";
import { isDefined } from "utils/fp";

import { IChartScreenshotSettings } from "models/chart";
import { EntityKind } from "models/entityKind";
import { WidgetComponentType } from "models/screenshot";

import {
  DEFAULT_IPDB_LABELS_SIZE,
  DEFAULT_IPDB_TITLE_SIZE
} from "components/map/shared/ipdbLegendDefaults";
import {
  useScreenshotContext,
  useScreenshotPresetContext
} from "components/screenshot/hooks";

function useMapScreenshotCalculatedDefaults(): IChartScreenshotSettings {
  // hooks
  const { widget } = useScreenshotContext();
  const { selectedPreset: preset } = useScreenshotPresetContext();
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const selectedShapefile = useSelector(
    (state: RootState) => state.project.selectedShapefile
  );

  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);
  const dashboardWidgets = useDashboardScreenshotStore((s) => s.activeWidgets);
  const dashboardHasMap = dashboardWidgets.some((w) => w.type === "map");

  return useMemo(() => {
    const isMap = widget?.widgetComponentType === WidgetComponentType.Map;
    const isDashboardWithMap =
      widget?.widgetComponentType === WidgetComponentType.Dashboard && dashboardHasMap;

    if (!isMap && !isDashboardWithMap) {
      return {};
    }

    return {
      showLegendInScreenshot: activeEntityKinds.includes(EntityKind.Well),
      showTownshipBorderInScreenshot: true,
      legendTitleSize: preset?.legendTitleSize ?? DEFAULT_FONT_SIZE,
      legendFontSize: preset?.legendFontSize ?? DEFAULT_FONT_SIZE,
      upstreamLegendOpacity: 1,
      mapLegendShowCounts: true,
      showLegendBorderInScreenshot: true,
      isWrappingTextMidstream: true,
      isWrappingTextUpstream: true,
      showMidstreamLegendInScreenshot: activeEntityKinds.includes(EntityKind.Facility),
      midstreamLegendOpacity: 1,
      midstreamLegendShowCounts: true,
      showMidstreamLegendBorderInScreenshot: false,
      mapScreenshotScale: 1,
      showIpdbLegendInScreenshot: true,
      showIpdbLegendBorderInScreenshot: true,
      ipdbTitleSize: DEFAULT_IPDB_TITLE_SIZE,
      ipdbLabelsSize: DEFAULT_IPDB_LABELS_SIZE,
      ipdbLegendOpacity: 1,
      applyAbbreviations: false,
      // EVA-127 "Simplify Custom Field Names" — default OFF.
      simplifyCustomFieldNames: false,
      showNetworkDepthLegendInScreenshot: networkDepthLegend?.legendItems?.length > 0,
      showNetworkDepthLegendBorderInScreenshot: true,
      networkDepthTitleSize: 15,
      networkDepthLabelsSize: 14,
      networkDepthLegendOpacity: 1,
      showShapefileLegendInScreenshot: false,
      shapefileLegendShowDetails: isDefined(selectedShapefile),
      showShapefileLegendBorderInScreenshot: true,
      isWrappingTextShapefile: true,
      shapefileTitleSize: preset?.legendTitleSize ?? DEFAULT_FONT_SIZE,
      shapefileLabelsSize: preset?.legendFontSize ?? DEFAULT_FONT_SIZE,
      shapefileLegendOpacity: 1
    };
  }, [
    preset?.legendTitleSize,
    preset?.legendFontSize,
    widget?.widgetComponentType,
    dashboardHasMap,
    networkDepthLegend,
    activeEntityKinds,
    selectedShapefile
  ]);
}

export default useMapScreenshotCalculatedDefaults;
